/* Evidence packs: what has been generated, what it covers, and the one action — generate.
 *
 * Three cards, as the redesign lays it out: the one action, with what it will read spelled out
 * before it is spent; then what frameworks exist; then what has been generated. Somebody who came to
 * hand an auditor a pack reads down and stops when they have it.
 *
 * **Unsigned is a state, not an error.** The hashes hold, the origin is unproven, and painting it red
 * would teach operators that unsigned means invalid — the exact collapse the backend's SealCheck
 * vocabulary refuses. The chip says the word and the line beneath says which case it is.
 *
 * **A narrower framework is not an incomplete one**, which is why every evidence-kind figure is
 * reported against six rather than as a bare count: a framework naming two kinds is about two
 * things, not missing four.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  can,
  GENERATE_PACK_PERMISSION,
  REGISTER_FRAMEWORK_PERMISSION,
  useIdentity,
} from '@/entities/session';
import type { EvidencePack, Framework } from '@/shared/api/generated/compliance';
import { Alert } from '@/shared/ui/Alert';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  downloadPack,
  useFrameworks,
  useGeneratePack,
  usePacks,
} from '../api/compliance';
import {
  asDay,
  EVIDENCE_KIND_TOTAL,
  evidenceKindCount,
  evidenceKindsOf,
  frameworkKey,
  packDraftIsValid,
  packHintKey,
  shortChecksum,
  SIGNING_TONE,
  signingLabelKey,
  signingOf,
} from '../model/packs';
import type { PackDraft } from '../model/packs';
import { RegisterFramework } from './RegisterFramework';

/** The default period: the last 30 days, which is the shape of a monthly evidence request.
 *
 * Pre-filled rather than blank because a blank pair would make the form's first state "choose a
 * framework *and* type two dates", and the period is the part somebody is least likely to get wrong
 * — they came for a named period. The hint still reports what will be read before they commit.
 */
function isoDaysAgo(days: number): string {
  const at = new Date();
  at.setDate(at.getDate() - days);
  return at.toISOString().slice(0, 10);
}

/** An outlined chip naming one evidence kind, with where it is collected from on the `title`.
 *
 * Outlined rather than tinted, matching the reference: these are not statuses. A tinted chip on this
 * screen means signing, and giving evidence kinds the same treatment would put two unrelated
 * vocabularies in one visual language.
 */
function KindChip({
  kind,
  small = false,
}: {
  readonly kind: string;
  readonly small?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      className={small ? 'lq-cp__kind lq-cp__kind--small' : 'lq-cp__kind'}
      title={t(`compliance.evidence.${kind}`)}
    >
      {t(`compliance.evidenceName.${kind}`)}
    </span>
  );
}

function GeneratePanel({
  frameworks,
  permitted,
  onGenerated,
}: {
  readonly frameworks: readonly Framework[];
  /** Whether this caller holds `generate:compliance.pack`. */
  readonly permitted: boolean;
  readonly onGenerated: (pack: EvidencePack) => void;
}): ReactElement {
  const { t } = useTranslation();
  const generate = useGeneratePack();
  const [draft, setDraft] = useState<PackDraft>({
    framework: '',
    periodStart: isoDaysAgo(30),
    periodEnd: isoDaysAgo(0),
  });

  const chosen = frameworks.find(
    (framework) => frameworkKey(framework) === draft.framework,
  );
  const valid = packDraftIsValid(draft, chosen);
  const kinds = chosen === undefined ? [] : evidenceKindsOf(chosen);

  return (
    <div className="lq-cp__gen">
      <div className="lq-cp__genGrid">
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('compliance.frameworkLabel')}</span>
          <select
            value={draft.framework}
            onChange={(event) => {
              setDraft((current) => ({ ...current, framework: event.target.value }));
            }}
          >
            <option value="">{t('compliance.frameworkPlaceholder')}</option>
            {frameworks.map((framework) => (
              <option key={frameworkKey(framework)} value={frameworkKey(framework)}>
                {t('compliance.frameworkOption', {
                  title: framework.title,
                  version: framework.version,
                  controls: framework.controls.length,
                  kinds: evidenceKindCount(framework),
                })}
              </option>
            ))}
          </select>
        </label>
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('compliance.periodStart')}</span>
          <input
            className="lq-mono"
            type="date"
            value={draft.periodStart}
            onChange={(event) => {
              setDraft((current) => ({ ...current, periodStart: event.target.value }));
            }}
          />
        </label>
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('compliance.periodEnd')}</span>
          <input
            className="lq-mono"
            type="date"
            value={draft.periodEnd}
            onChange={(event) => {
              setDraft((current) => ({ ...current, periodEnd: event.target.value }));
            }}
          />
        </label>
        <button
          type="button"
          className="lq-cp__generate"
          title={permitted ? undefined : t('compliance.notPermitted.generate')}
          disabled={!permitted || !valid || generate.isPending}
          onClick={() => {
            if (chosen === undefined) return;
            generate.mutate(
              {
                frameworkCode: chosen.code,
                frameworkVersion: chosen.version,
                periodStart: draft.periodStart,
                periodEnd: draft.periodEnd,
              },
              { onSuccess: onGenerated },
            );
          }}
        >
          {t('compliance.generate')}
        </button>
      </div>

      <div className="lq-inlineform__hint">
        {permitted
          ? t(packHintKey(draft, chosen), {
              kinds: kinds.length,
              controls: chosen?.controls.length ?? 0,
              start: draft.periodStart,
              end: draft.periodEnd,
            })
          : t('compliance.notPermitted.generate')}
      </div>

      {generate.isError && (
        <Alert tone="error" title={t('compliance.generateFailed')}>
          {generate.error.message}
        </Alert>
      )}

      {/* What the chosen framework actually draws on, before generating rather than after. A pack is
          fixed at generation, so "this reads six kinds and I wanted the two about approvals" is a
          sentence somebody needs to be able to think before they press the button. */}
      {chosen !== undefined && (
        <div className="lq-cp__chosen">
          <div className="lq-cp__chosenTitle">
            {t('compliance.chosenTitle', {
              code: chosen.code,
              version: chosen.version,
              kinds: kinds.length,
              total: EVIDENCE_KIND_TOTAL,
            })}
          </div>
          <div className="lq-cp__kinds">
            {kinds.map((kind) => (
              <KindChip key={kind} kind={kind} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FrameworkRow({ framework }: { readonly framework: Framework }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = (): void => {
    /* Both segments encoded. A framework code is a published identifier — `RBI-MD-IT` — and a
       version is dotted, so neither needs escaping today; encoding anyway is what stops the first
       framework code containing a slash from producing a route nobody can debug. */
    navigate(
      `/compliance/${encodeURIComponent(framework.code)}/${encodeURIComponent(framework.version)}`,
    );
  };

  return (
    /* The whole row opens the framework, as the reference has it. The keyboard route is the Controls
       control in the last cell rather than anything on the row: a `tr` with a `tabIndex` announces
       itself as a control and is not one. */
    <tr className="lq-cp__row" onClick={open}>
      <td data-label={t('compliance.column.framework')}>
        <strong className="lq-cp__fwTitle">{framework.title}</strong>
        <div className="lq-cell__secondary">{framework.description}</div>
      </td>
      <td className="lq-mono lq-nowrap" data-label={t('compliance.column.version')}>
        {framework.code} {framework.version}
      </td>
      <td className="lq-nowrap" data-label={t('compliance.column.controls')}>
        {t('compliance.controlCount', { count: framework.controls.length })}
      </td>
      <td className="lq-nowrap" data-label={t('compliance.column.kinds')}>
        {t('compliance.ofTotal', {
          count: evidenceKindCount(framework),
          total: EVIDENCE_KIND_TOTAL,
        })}
      </td>
      <td
        className="lq-cp__muted lq-nowrap"
        data-label={t('compliance.column.registered')}
      >
        {/* The exact instant on the title: a registration is read as a day, and an auditor asking
            when a framework version came into force wants the timestamp the API sent. */}
        <span title={framework.registered_at}>
          {t('compliance.registeredOn', {
            day: asDay(framework.registered_at),
            who: personName(framework.registered_by),
          })}
        </span>
      </td>
      <td className="lq-table__actions">
        <button
          type="button"
          className="lq-rowaction"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          {t('compliance.openControls')}
        </button>
      </td>
    </tr>
  );
}

function PackRow({
  pack,
  busy,
  onDownload,
}: {
  readonly pack: EvidencePack;
  /** A download of *this* pack is in flight. */
  readonly busy: boolean;
  readonly onDownload: (pack: EvidencePack) => void;
}): ReactElement {
  const { t } = useTranslation();
  const state = signingOf(pack);

  return (
    <tr className="lq-cp__packRow">
      <td className="lq-nowrap" data-label={t('compliance.column.framework')}>
        <span className="lq-mono">{pack.framework_code}</span> {pack.framework_version}
      </td>
      <td className="lq-nowrap" data-label={t('compliance.column.period')}>
        <span className="lq-cp__period">
          {pack.period_start} → {pack.period_end}
        </span>
        {/* Said rather than implied: the period is what the pack evidences and it cannot move, so a
            reader comparing two packs over the same dates is comparing two artifacts. */}
        <div className="lq-cell__secondary">{t('compliance.periodFixed')}</div>
      </td>
      <td
        className="lq-cp__muted lq-nowrap"
        data-label={t('compliance.column.generatedBy')}
      >
        {/* A person and how long ago, as the design reads it; the exact instant and the subject the
            audit chain records are on the title for whoever is reconciling against it. */}
        <span title={`${pack.generated_by} · ${pack.generated_at}`}>
          {t('compliance.generatedByAgo', {
            who: personName(pack.generated_by),
            age: ageText(t, pack.generated_at, new Date()) ?? pack.generated_at,
          })}
        </span>
      </td>
      <td data-label={t('compliance.column.signing')}>
        <span
          className={`lq-tag lq-tag--sign-${SIGNING_TONE[state]}`}
          title={
            state === 'signed'
              ? (pack.signing_key ?? '')
              : t('compliance.signing.unsignedMeaning')
          }
        >
          <span className="lq-tag__dot" aria-hidden="true" />
          {t(signingLabelKey(pack))}
        </span>
        {/* The key on the row rather than only on hover, because "which key signed this" is the
            question an auditor asks of a signed pack — and for an unsigned one the same line says
            there is no key on this deployment, which is a deployment fact and not a pack fault. */}
        <div className="lq-cp__signKey">
          {pack.signing_key ?? t('compliance.signing.noKey')}
        </div>
      </td>
      <td data-label={t('compliance.column.checksum')}>
        <span
          className="lq-cp__checksum lq-mono"
          title={t('compliance.checksumTitle', { checksum: pack.checksum })}
        >
          {shortChecksum(pack.checksum)}
        </span>
      </td>
      <td className="lq-table__actions">
        {/* A button, not an anchor. A browser navigation carries no `Authorization` header, so the
            href this used to be answered 401 and replaced the app with the API's problem document —
            the bearer token lives in this process's memory under ADR-012's local auth. */}
        <button
          type="button"
          className="lq-rowaction"
          disabled={busy}
          onClick={() => {
            onDownload(pack);
          }}
        >
          {t('compliance.download')}
        </button>
      </td>
    </tr>
  );
}

export function CompliancePacks(): ReactElement {
  const { t } = useTranslation();
  const frameworks = useFrameworks();
  const packs = usePacks();
  /* Read once and asked twice. Both writes on this screen reach `approver` and `admin` only, and
     `can` denies while the identity is still loading — so a control never appears and then vanishes,
     which reads as a bug. */
  const identity = useIdentity();
  const mayGenerate = can(identity.data, GENERATE_PACK_PERMISSION);
  const mayRegister = can(identity.data, REGISTER_FRAMEWORK_PERMISSION);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    readonly titleKey: string;
    readonly bodyKey: string;
    readonly values: Record<string, string>;
  } | null>(null);

  const frameworkItems = frameworks.isSuccess ? frameworks.data : [];
  const packItems = packs.isSuccess ? packs.data : [];

  const download = (started: EvidencePack): void => {
    setDownloading(started.id);
    setMessage(null);
    void downloadPack(started)
      .then((outcome) => {
        /* Three outcomes and three sentences. `mismatched` is the one that matters — the bytes hashed
           to something other than the checksum recorded when they were written, which is object
           storage returning something other than what was put there. The file is saved either way:
           somebody investigating a discrepancy needs the bytes most when the bytes look wrong. */
        setMessage({
          titleKey: `compliance.acted.download.${outcome}`,
          bodyKey: `compliance.acted.downloadBody.${outcome}`,
          values: { id: started.id, checksum: shortChecksum(started.checksum) },
        });
      })
      .catch((failure: unknown) => {
        /* The server's own words. A 403 here is a permission answer, and this screen must not
           restate it as "the download failed". */
        setMessage({
          titleKey: 'compliance.acted.download.failed',
          bodyKey: 'compliance.acted.downloadBody.failed',
          values: {
            id: started.id,
            detail: failure instanceof Error ? failure.message : String(failure),
          },
        });
      })
      .finally(() => {
        setDownloading(null);
      });
  };

  return (
    <div className="lq-cp">
      <PageHeader
        title={t('compliance.title')}
        help={t('help.feature.compliance')}
        lede={t('compliance.description')}
        meta={
          frameworks.isSuccess && packs.isSuccess ? (
            <span>
              {t('compliance.meta.frameworks', { count: frameworkItems.length })}
              {' · '}
              {t('compliance.meta.packs', { count: packItems.length })}
            </span>
          ) : undefined
        }
      />

      {message !== null && (
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">
            {t(message.titleKey, message.values)}
          </strong>
          <span className="lq-flash__body">{t(message.bodyKey, message.values)}</span>
        </div>
      )}

      <section className="lq-cp__card" aria-labelledby="cp-generate">
        <h2 id="cp-generate" className="lq-cp__cardTitle">
          {t('compliance.section.generate')}
        </h2>

        {frameworks.isPending && <SkeletonRows rows={2} columns={4} />}

        {frameworks.isError && (
          <Alert tone="error" title={t('compliance.loadFailed')}>
            {frameworks.error.message}
          </Alert>
        )}

        {frameworks.isSuccess &&
          (frameworkItems.length === 0 ? (
            <EmptyState
              title={t('compliance.noFrameworks.title')}
              guidance={t('compliance.noFrameworks.body')}
            />
          ) : (
            <GeneratePanel
              frameworks={frameworkItems}
              permitted={mayGenerate}
              onGenerated={(pack) => {
                /* Two sentences, chosen by whether a key signed the pack. One template with the
                   key interpolated read "signed with No signing key on this deployment" for every
                   unsigned pack — a claim of a signature in the one case where there is none.
                   And no pack id in the title: the framework and period are what a reader
                   recognises, and the id is on the row below for whoever needs it. */
                setMessage({
                  titleKey: 'compliance.acted.generated',
                  bodyKey:
                    pack.signing_key === null
                      ? 'compliance.acted.generatedBody.unsigned'
                      : 'compliance.acted.generatedBody.signed',
                  values: {
                    code: pack.framework_code,
                    version: pack.framework_version,
                    start: pack.period_start,
                    end: pack.period_end,
                    key: pack.signing_key ?? '',
                  },
                });
              }}
            />
          ))}
      </section>

      {frameworks.isSuccess && (
        <section
          className="lq-cp__card lq-cp__card--table"
          aria-labelledby="cp-frameworks"
        >
          <div className="lq-cp__cardHead">
            <h2 id="cp-frameworks" className="lq-cp__cardTitle">
              {t('compliance.section.frameworks')}
            </h2>
            {/* Absent rather than disabled without the permission — a reader who cannot register
                anything does not need a door they cannot open, and the empty state already explains
                where frameworks come from. */}
            {mayRegister ? (
              <button
                type="button"
                className="lq-cp__headButton"
                aria-expanded={registerOpen}
                onClick={() => {
                  setRegisterOpen((open) => !open);
                  setMessage(null);
                }}
              >
                {t(registerOpen ? 'compliance.reg.close' : 'compliance.reg.open')}
              </button>
            ) : null}
          </div>
          {registerOpen && (
            <RegisterFramework
              existing={frameworkItems}
              onClose={() => {
                setRegisterOpen(false);
              }}
              onRegistered={(framework) => {
                setRegisterOpen(false);
                setMessage({
                  titleKey: 'compliance.acted.registered',
                  bodyKey: 'compliance.acted.registeredBody',
                  values: { code: framework.code, version: framework.version },
                });
              }}
            />
          )}
          {frameworkItems.length > 0 && (
            <div className="lq-cp__scroll">
              <table className="lq-table lq-table--ledger lq-cp__table">
                <caption className="lq-visually-hidden">
                  {t('compliance.frameworksCaption')}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('compliance.column.framework')}</th>
                    <th scope="col">{t('compliance.column.version')}</th>
                    <th scope="col">{t('compliance.column.controls')}</th>
                    <th scope="col">{t('compliance.column.kinds')}</th>
                    <th scope="col">{t('compliance.column.registered')}</th>
                    <th scope="col" className="lq-table__actions" />
                  </tr>
                </thead>
                <tbody>
                  {frameworkItems.map((framework) => (
                    <FrameworkRow key={frameworkKey(framework)} framework={framework} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="lq-cp__card lq-cp__card--table" aria-labelledby="cp-packs">
        <div className="lq-cp__cardHead">
          <h2 id="cp-packs" className="lq-cp__cardTitle">
            {t('compliance.section.packs')}
          </h2>
        </div>

        {packs.isPending && <SkeletonRows rows={3} columns={6} />}

        {packs.isError && (
          <Alert tone="error" title={t('compliance.packsFailed')}>
            {packs.error.message}
          </Alert>
        )}

        {packs.isSuccess &&
          (packItems.length === 0 ? (
            /* Nothing is wrong: there is no schedule that produces packs, so an empty list is the
               normal state of a deployment nobody has asked for evidence from yet. */
            <div className="lq-cp__none">
              <div className="lq-cp__noneTitle">{t('compliance.empty.title')}</div>
              <div className="lq-cp__noneBody">{t('compliance.empty.body')}</div>
            </div>
          ) : (
            <div className="lq-cp__scroll">
              <table className="lq-table lq-table--ledger lq-cp__table">
                <caption className="lq-visually-hidden">
                  {t('compliance.packsCaption')}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('compliance.column.framework')}</th>
                    <th scope="col">{t('compliance.column.period')}</th>
                    <th scope="col">{t('compliance.column.generatedBy')}</th>
                    <th scope="col">{t('compliance.column.signing')}</th>
                    <th scope="col">{t('compliance.column.checksum')}</th>
                    <th scope="col" className="lq-table__actions" />
                  </tr>
                </thead>
                <tbody>
                  {packItems.map((pack) => (
                    <PackRow
                      key={pack.id}
                      pack={pack}
                      busy={downloading === pack.id}
                      onDownload={download}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </section>
    </div>
  );
}
